import React, { forwardRef } from 'react';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';

import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import { DEFAULT_SALON_PUBLIC_LAYOUT } from '../../../theme/salonPublicLayout';


const TONE_COLORS = Object.freeze({
  primary: 'primary',
  secondary: 'secondary',
  functional: 'info',
  info: 'info',
  success: 'success',
  warning: 'warning',
  destructive: 'error',
  error: 'error',
});

export const resolveSalonButtonColor = (tone) => TONE_COLORS[tone] || 'primary';

const asSxArray = (sx) => {
  if (!sx) return [];
  return Array.isArray(sx) ? sx : [sx];
};

const SalonPublicButton = forwardRef(({
  tone = 'primary',
  variant,
  preserveLegacyStyle = false,
  style,
  sx,
  ...props
}, ref) => {
  const { layout, tokens: theme } = useSalonPublicTheme();
  const buttonTokens = layout.tokens.buttons;
  const color = resolveSalonButtonColor(tone);
  const defaultButtons = DEFAULT_SALON_PUBLIC_LAYOUT.buttons;
  const usesCompleteDefaultPreset = buttonTokens.appearance === defaultButtons.appearance
    && buttonTokens.shape === defaultButtons.shape
    && buttonTokens.size === defaultButtons.size;
  const hasCustomColors = [
    buttonTokens.backgroundColor,
    buttonTokens.borderColor,
    buttonTokens.textColor,
  ].some((value) => value !== 'inherit');
  const hasCustomTypography = Object.keys(
    buttonTokens.styleOverrides || {},
  ).length > 0;
  const hasCustomStyle = hasCustomColors || hasCustomTypography;
  const preserveProductionButton = preserveLegacyStyle
    && usesCompleteDefaultPreset
    && !hasCustomStyle;
  const customColorSx = hasCustomColors ? {
    ...(buttonTokens.backgroundColor !== 'inherit' && {
      backgroundColor: buttonTokens.backgroundColor,
    }),
    ...(buttonTokens.borderColor !== 'inherit' && {
      borderColor: buttonTokens.borderColor,
    }),
    ...(buttonTokens.textColor !== 'inherit' && {
      color: buttonTokens.textColor,
    }),
    '&:hover': {
      ...(buttonTokens.backgroundColor !== 'inherit' && {
        backgroundColor: buttonTokens.backgroundColor,
      }),
      ...(buttonTokens.borderColor !== 'inherit' && {
        borderColor: buttonTokens.borderColor,
      }),
      ...(buttonTokens.textColor !== 'inherit' && {
        color: buttonTokens.textColor,
      }),
    },
  } : {};

  return (
    <Button
      {...props}
      ref={ref}
      color={color}
      variant={preserveProductionButton ? variant : buttonTokens.variant}
      disableElevation
      data-salon-button-appearance={buttonTokens.appearance}
      data-salon-button-shape={buttonTokens.shape}
      data-salon-button-size={buttonTokens.size}
      data-salon-button-tone={tone}
      data-salon-button-customized={String(hasCustomStyle)}
      style={{
        ...style,
        ...(preserveProductionButton ? {} : {
          borderRadius: buttonTokens.borderRadius,
          minHeight: buttonTokens.minHeight,
          paddingBlock: buttonTokens.paddingBlock,
          paddingInline: buttonTokens.paddingInline,
        }),
      }}
      sx={[
        {
          textTransform: 'none',
          '&.Mui-focusVisible': {
            outline: `3px solid ${theme.salon.focus}`,
            outlineOffset: '2px',
          },
        },
        !preserveProductionButton && buttonTokens.appearance === 'soft' && ((muiTheme) => ({
          backgroundColor: alpha(muiTheme.palette[color].main, 0.12),
          color: muiTheme.palette[color].main,
          '&:hover': {
            backgroundColor: alpha(muiTheme.palette[color].main, 0.2),
          },
        })),
        !preserveProductionButton && buttonTokens.styleOverrides,
        !preserveProductionButton && customColorSx,
        ...asSxArray(sx),
      ]}
    />
  );
});

SalonPublicButton.displayName = 'SalonPublicButton';

export default SalonPublicButton;
